import {
  Station,
  Binding,
  Filters,
  FormOptions,
  BulkBindingRequest,
  BulkBindingResponse
} from '@/types/azk-binding.types';
import { ApiResponse } from '@/types/services';

import { baseApi } from './base-api';
import { buildQueryParams } from './helpers';

export interface GetOkkoStationsParams {
  search?: string;
}

export interface GetCompetitorStationsParams {
  region?: string;
  province?: string;
  brand?: string;
  search?: string;
}

export interface GetCompetitorFiltersParams {
  region?: string;
  province?: string;
  brand?: string;
}

export interface GetBindingsForOkkoParams {
  okkoStationUnicode: string;
}

export interface GetBindingsForCompetitorParams {
  competitorStationUnicode: string;
}

export const azkBindingApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getOkkoStations: builder.query<Station[], GetOkkoStationsParams>({
      query: ({ search }) => {
        const queryString = buildQueryParams({ search });
        return { url: `azk-binding/okko-stations${queryString ? `?${queryString}` : ''}` };
      },
      providesTags: ['AzkOkkoStations'],
      transformResponse: (response: ApiResponse<Station[]>) => {
        return response?.data || [];
      }
    }),

    getCompetitorStations: builder.query<Station[], GetCompetitorStationsParams>({
      query: params => {
        const queryString = buildQueryParams(params);
        return { url: `azk-binding/competitor-stations${queryString ? `?${queryString}` : ''}` };
      },
      providesTags: ['AzkCompetitorStations'],
      transformResponse: (response: ApiResponse<Station[]>) => {
        return response?.data || [];
      }
    }),

    getCompetitorFilters: builder.query<Filters, GetCompetitorFiltersParams>({
      query: params => {
        const queryString = buildQueryParams(params);
        return { url: `azk-binding/competitor-filters${queryString ? `?${queryString}` : ''}` };
      },
      providesTags: ['AzkCompetitorFilters'],
      transformResponse: (response: ApiResponse<Filters>) => {
        return {
          regions: ['Всі', ...(response?.data?.regions || [])],
          province: ['Всі', ...(response?.data?.province || [])],
          brands: ['Всі', ...(response?.data?.brands || [])]
        };
      }
    }),

    getBindingsForOkko: builder.query<Binding[], GetBindingsForOkkoParams>({
      query: ({ okkoStationUnicode }) => ({
        url: `azk-binding/okko/${okkoStationUnicode}`
      }),
      providesTags: (_result, _error, { okkoStationUnicode }) => [
        { type: 'AzkBindings', id: `okko-${okkoStationUnicode}` }
      ],
      transformResponse: (response: ApiResponse<Binding[]>) => {
        return response?.data || [];
      }
    }),

    getBindingsForCompetitor: builder.query<Binding[], GetBindingsForCompetitorParams>({
      query: ({ competitorStationUnicode }) => ({
        url: `azk-binding/competitor/${competitorStationUnicode}`
      }),
      providesTags: (_result, _error, { competitorStationUnicode }) => [
        { type: 'AzkBindings', id: `competitor-${competitorStationUnicode}` }
      ],
      transformResponse: (response: ApiResponse<Binding[]>) => {
        return response?.data || [];
      }
    }),

    bulkUpdateBindings: builder.mutation<BulkBindingResponse, BulkBindingRequest>({
      query: request => ({
        url: 'azk-binding/upsert',
        method: 'POST',
        body: request
      }),
      invalidatesTags: (_result, _error, request) => {
        const competitorTags = Array.from(
          new Set(request.bindings.map(binding => binding.competitorStationUnicode))
        ).map(competitorUnicode => ({ type: 'AzkBindings' as const, id: `competitor-${competitorUnicode}` }));

        return [{ type: 'AzkBindings' as const, id: `okko-${request.okkoStationUnicode}` }, ...competitorTags];
      }
    }),

    deleteBinding: builder.mutation<void, number>({
      query: bindingId => ({
        url: `azk-binding/${bindingId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['AzkBindings']
    }),

    getFormOptions: builder.query<FormOptions, void>({
      query: () => ({ url: 'azk-binding/form-options' }),
      providesTags: ['AzkFormOptions'],
      transformResponse: (response: FormOptions) => {
        return {
          influence_types: response?.influence_types || [],
          location_types: response?.location_types || []
        };
      }
    })
  })
});

export const {
  useGetOkkoStationsQuery,
  useGetCompetitorStationsQuery,
  useGetCompetitorFiltersQuery,
  useGetBindingsForOkkoQuery,
  useGetBindingsForCompetitorQuery,
  useBulkUpdateBindingsMutation,
  useDeleteBindingMutation,
  useGetFormOptionsQuery
} = azkBindingApi;
